import { api } from "./client";

export type DraftDocument = Record<string, unknown>;
export interface TeachingDraftAck {
  id: string;
  course_id: string;
  revision: number;
  state: "active" | "discarded" | "published";
  assignment_id: string | null;
  base_assignment_version_id?: string | null;
  base_assignment_updated_at?: string | null;
  published_assignment_id: string | null;
  published_version_id: string | null;
  /** The activity this draft began from: linked, unavailable (the activity was removed), or none. */
  origin?: ActivityOriginState | null;
}
export type ActivityOrigin = { unit_id: string; activity_id: string };
export type ActivityOriginState = { status: "unavailable" } | { status: "linked"; id: string; unit_id: string | null; activity_id: string; draft_id: string | null; assignment_id: string | null; state: "active" | "unlinked"; version: number };
/** An attached course material as the author reads it now; a removed one keeps its place with no material. */
export interface DraftMaterial {
  position: number;
  material_id: string | null;
  title?: string | null;
  file_url?: string | null;
  source?: "drive" | "link" | null;
}
export interface TeachingDraft extends TeachingDraftAck {
  /** The saved revision's attached course materials, in brief order. */
  materials?: DraftMaterial[];
  document: DraftDocument;
  parked: DraftDocument;
  base_assignment_updated_at: string | null;
  updated_at: string;
  saved_at: string;
  saved_by: string;
}
export interface SaveTeachingDraft {
  expected_revision: number;
  document: DraftDocument;
  parked: DraftDocument;
  assignment_id?: string | null;
  expected_assignment_updated_at?: string | null;
  expected_assignment_version_id?: string | null;
  /** Ordered existing course materials; omitted keeps the saved set. */
  material_ids?: string[];
  /** Sent only with a new draft's first save: the activity it begins from. Never part of the document. */
  origin?: ActivityOrigin;
}
export type RebaseStrategy = "keep_draft" | "use_current";
/** A list row: who last saved it, when, and which published version it corrects (S1A-6). */
export type TeachingDraftHeader = Pick<TeachingDraft, "id" | "course_id" | "assignment_id" | "revision" | "state" | "updated_at" | "saved_by" | "saved_at"> & { title: string | null; base_version_no: number | null; /** The session plan the draft is placed in, when it names one. */ unit_id?: string | null };
const path = (course: string, id?: string) => `/courses/${encodeURIComponent(course)}/teaching-drafts${id ? `/${encodeURIComponent(id)}` : ""}`;
const keyed = (key: string) => ({ headers: { "Idempotency-Key": key } });
export const teachingDraftsApi = {
  list: (course: string, offset = 0) => api.get<{ items: TeachingDraftHeader[]; next_offset: number | null }>(`${path(course)}?offset=${offset}`, { cache: "no-store" }),
  get: (course: string, id: string) => api.get<TeachingDraft>(path(course, id), { cache: "no-store" }),
  save: (course: string, id: string, body: SaveTeachingDraft, key: string) => api.put<TeachingDraftAck>(path(course, id), body, keyed(key)),
  rebase: (course: string, id: string, expected_revision: number, expected_assignment_version_id: string, strategy: RebaseStrategy, key: string) => api.post<TeachingDraft>(`${path(course, id)}/rebase`, { expected_revision, expected_assignment_version_id, strategy }, keyed(key)),
  publish: (course: string, id: string, expected_revision: number, reason: string | null, key: string) => api.post<TeachingDraftAck>(`${path(course, id)}/publish`, { expected_revision, reason }, keyed(key)),
  discard: (course: string, id: string, expected_revision: number, key: string) => api.post<TeachingDraftAck>(`${path(course, id)}/discard`, { expected_revision }, keyed(key)),
  unlinkActivity: (course: string, link: string, expected_version: number, key: string) => api.post<ActivityOriginState>(`/courses/${encodeURIComponent(course)}/activity-coursework/${encodeURIComponent(link)}/unlink`, { expected_version }, keyed(key)),
  restore: (course: string, id: string, expected_revision: number, key: string) => api.post<TeachingDraftAck>(`${path(course, id)}/restore`, { expected_revision }, keyed(key)),
};
